<script setup lang="ts">
import type { Template } from '~/utils/solr/template'
import { faArrowDownToBracket, faLockKeyhole } from '@fortawesome/pro-light-svg-icons'
import { formatAuthors } from '~/utils/authors'
import { getTemplateDocxKey } from '#shared/utils/template-files'
import { toTemplateRoutePath } from '~/utils/templates/template-files'

defineProps<{
  templates: Template[]
  isFavorite?: (category: 'law' | 'templates' | 'journals' | 'literature', id: string) => boolean
  isFavoriteEnabled?: boolean
  isLoading?: boolean
  hasActiveFilters?: boolean
  toggleFavorite?: (id: string) => void
}>()

const hasLicense = useHasContentLicense()
const { downloadMainTemplate } = useTemplateDownload()
</script>

<template>
  <Table aria-label="Maler" class="flex flex-col w-full">
    <template #rows>
      <TableBody class="space-y-2xs">
        <template v-if="isLoading">
          <TableRow v-for="n in 10" :key="`skeleton-${n}`" is-loading class="p-s">
            <TableData class="gap-x-m">
              <Skeleton class="h-8 bg-carbon-200 rounded-2xl md:w-8/12" />
              <Skeleton class="h-8  bg-carbon-200 rounded-2xl md:w-3/12" />
              <Skeleton class="h-8 bg-carbon-200 rounded-2xl md:w-1/12" />
            </TableData>
          </TableRow>
        </template>

        <template v-else>
          <TableRow v-for="template in templates" :key="template.id">
            <NuxtLink :to="toTemplateRoutePath(template)" class="group w-full flex items-center no-underline px-s py-xs gap-s md:gap-m min-h-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blurple-300">
              <div class="flex w-full flex-col md:flex-row justify-center md:justify-start gap-4xs md:gap-0 md:w-8/12">
                <h3 class="title-s font-semibold">
                  {{ template.title }}
                </h3>
                <div v-if="template.authorName?.length || template.lastUpdatedByEditor" class="md:hidden text-carbon-400 text-xs font-normal">
                  <div v-if="template.authorName?.length">
                    {{ formatAuthors(template.authorName) }}
                  </div>
                  <div v-if="template.lastUpdatedByEditor">
                    {{ `Sist oppdatert ${formatDate(template.lastUpdatedByEditor[0], { year: 'numeric', month: 'long', day: 'numeric' })}` }}
                  </div>
                </div>
              </div>

              <div class="hidden md:flex w-full flex-col text-carbon-400 text-xs gap-3xs md:w-3/12">
                <span v-if="template.authorName?.length">
                  {{ formatAuthors(template.authorName) }}
                </span>
                <span v-if="template.lastUpdatedByEditor">
                  {{ `Sist oppdatert ${formatDate(template.lastUpdatedByEditor[0], { year: 'numeric', month: 'long', day: 'numeric' })}` }}
                </span>
              </div>

                <div class="flex justify-end items-center gap-s md:w-1/12">
                  <template v-if="hasLicense">
                    <button
                      type="button"
                      class="p-2xs rounded hover:bg-surface-secondary disabled:opacity-40 disabled:hover:bg-transparent"
                      :disabled="!getTemplateDocxKey(template.files)"
                      :aria-label="`Last ned ${template.title ?? 'mal'} (docx)`"
                      @click.stop.prevent="downloadMainTemplate(template, 'list')"
                    >
                      <Icon :icon="faArrowDownToBracket" />
                    </button>

                    <FavoriteButton
                      v-if="toggleFavorite"
                      intent="outlined"
                      :selected="isFavorite ? isFavorite('templates', template.id) : false"
                      :aria-label="isFavorite?.('templates', template.id) ? `Fjern ${template.title} fra favoritter` : `Legg til ${template.title} i favoritter`"
                      @click.prevent="toggleFavorite(template.id)"
                    />
                  </template>
                  <Icon v-else :icon="faLockKeyhole" />
                </div>
            </NuxtLink>
          </TableRow>

          <div v-if="!templates?.length" class="py-m opacity-70">
            {{ hasActiveFilters ? 'Fant ingen maler som samsvarer med filtrene dine.' : 'Fant ingen maler.' }}
          </div>
        </template>
      </TableBody>
    </template>
  </Table>
</template>
